import { useBreakpointValue, type SlideProps } from "@chakra-ui/react";
import { useDeviceSize, useReturningUsersNotification, useScrollDirection } from "hooks";
import { useEffect, useState } from "react";
import { useMobileMenuStore } from "stores/MobileMenuStore";
import { useRouter } from "next/router";
import type { UseHeaderAnimationReturnType } from "./types";

/**
 * Custom React hook that provides animation styles of the header component.
 * @returns An object that contains the animation styles of the header component.
 */
export const useHeaderAnimation = (): UseHeaderAnimationReturnType => {
  const { isDesktop } = useDeviceSize();
  const [navbarPosition, setNavbarPosition] = useState<"sticky" | "fixed">("fixed");
  const [navbarZIndex, setNavbarZIndex] = useState<3 | 1300 | 4000>(4000);
  const { isOpen: isMobileMenuOpen } = useMobileMenuStore();
  const { isOpen: isNotificationVisible, onClose } = useReturningUsersNotification();

  useEffect(() => {
    setNavbarPosition(isNotificationVisible ? "sticky" : "fixed");
    setNavbarZIndex(isMobileMenuOpen ? (isNotificationVisible ? 1300 : 4000) : 3);
  }, [isNotificationVisible, isMobileMenuOpen, onClose]);

  const { scrollDirection, isScrollUp } = useScrollDirection();
  const navBoxShadow = isScrollUp ? "1px 4px 10px rgba(203, 202, 209, 0.25)" : "none";

  const slideStyles = {
    style: { zIndex: navbarZIndex, position: navbarPosition, marginBottom: isDesktop ? "-100px" : "-60px" },
    initial: "visible",
    direction: "top",
    in: useBreakpointValue({ base: isScrollUp, lg: true }),
  } as SlideProps;

  return {
    slideStyles,
    navBoxShadow,
    scrollDirection,
  };
};

export const useCloseOnNavigation = (closeMenuCallback: () => void) => {
  const router = useRouter();
  useEffect(() => {
    router.events.on("routeChangeStart", closeMenuCallback);

    return () => {
      router.events.off("routeChangeStart", closeMenuCallback);
    };
  }, [router, closeMenuCallback]);
};
